import { useQuery } from "@tanstack/react-query";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { TOKEN_CONFIG } from "@/lib/config";
import Icon from "@/components/shared/Icon";
import Seo from "@/components/shared/Seo";
import PageHero from "@/components/shared/PageHero";
import StatusBadge from "@/components/shared/StatusBadge";

const STAGES = ["Planned", "Awaiting verification", "Pending", "Confirmed", "Failed"];

function Metric({ icon, label, value, note }) {
  return (
    <div className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-6">
      <Icon name={icon} className="h-7 w-7 text-[#147DEB]" />
      <p className="mt-3 text-sm font-semibold text-[#64748B]">{label}</p>
      <p className="font-display mt-1 text-2xl font-extrabold text-[#082B66]">{value}</p>
      {note && <p className="mt-1 text-xs text-[#94A3B8]">{note}</p>}
    </div>
  );
}

export default function Impact() {
  const { t, code } = useLang();
  const { data: summary } = useQuery({ queryKey: ["impact-summary"], queryFn: () => api.get("/impact/summary").then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ["impact-projects"], queryFn: () => api.get("/impact/projects").then((r) => r.data) });
  const { data: donations = [] } = useQuery({ queryKey: ["impact-donations"], queryFn: () => api.get("/impact/donations").then((r) => r.data) });
  const { data: reports = [] } = useQuery({ queryKey: ["impact-reports"], queryFn: () => api.get("/impact/reports").then((r) => r.data) });

  return (
    <>
      <Seo title={t("impact.title")} description={t("impact.subtitle")} />
      <PageHero eyebrow={t("nav.impact")} title={t("impact.title")} subtitle={t("impact.subtitle")} tone="navy" />

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Pre-launch honesty banner */}
        <div className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-6" data-testid="impact-prelaunch-banner">
          <div className="flex items-start gap-3">
            <Icon name="Info" className="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-600" />
            <div>
              <h2 className="font-display text-lg font-bold text-amber-900">{t("impact.prelaunch.title")}</h2>
              <p className="mt-1 text-sm leading-relaxed text-amber-800">{t("impact.prelaunch.desc")}</p>
            </div>
          </div>
        </div>

        {/* Metrics — honest, never fabricated */}
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Metric icon="BadgeCheck" label={t("impact.confirmedTotal")} value={summary?.confirmed_total ?? t("common.notVerified")} />
          <Metric icon="Users" label={t("impact.childrenHelped")} value={summary?.children_helped ?? t("common.notVerified")} />
          <Metric icon="Wallet" label={t("impact.treasuryAddr")} value={TOKEN_CONFIG.treasuryAddress ?? t("common.comingSoon")} note={t("impact.treasury")} />
          <Metric icon="Hourglass" label={t("impact.donations")} value={summary?.total_records ?? 0} />
        </div>

        {/* Stage legend */}
        <div className="mt-10">
          <h3 className="font-display text-xl font-bold text-[#082B66]">{t("impact.statuses")}</h3>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {STAGES.map((s) => (
              <StatusBadge key={s} status={s} />
            ))}
          </div>
        </div>

        {/* Projects */}
        <div className="mt-12">
          <h3 className="font-display text-xl font-bold text-[#082B66]">{t("impact.projects")}</h3>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            {projects.map((p) => (
              <div key={p.slug} className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-6" data-testid={`impact-project-${p.slug}`}>
                <div className="flex items-start justify-between gap-3">
                  <h4 className="font-display text-lg font-bold text-[#082B66]">{code === "zh" ? p.title_zh : p.title_en}</h4>
                  <StatusBadge status={p.status} />
                </div>
                <p className="mt-2 text-sm leading-relaxed text-[#475569]">{code === "zh" ? p.summary_zh : p.summary_en}</p>
                <dl className="mt-4 space-y-1.5 text-sm">
                  <div className="flex gap-2"><dt className="font-semibold text-[#64748B]">{t("impact.region")}:</dt><dd className="text-[#082B66]">{code === "zh" ? p.region_zh : p.region_en}</dd></div>
                  <div className="flex gap-2"><dt className="font-semibold text-[#64748B]">{t("impact.recipient")}:</dt><dd className="text-[#082B66]">{code === "zh" ? p.recipient_zh : p.recipient_en}</dd></div>
                </dl>
                <p className={`mt-3 inline-flex items-center gap-1.5 text-xs font-bold ${p.recipient_verified ? "text-emerald-600" : "text-amber-600"}`}>
                  <Icon name={p.recipient_verified ? "ShieldCheck" : "ShieldAlert"} className="h-4 w-4" />
                  {p.recipient_verified ? t("impact.verified") : t("impact.unverified")}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Donation records */}
        <div className="mt-12">
          <h3 className="font-display text-xl font-bold text-[#082B66]">{t("impact.donations")}</h3>
          {donations.length === 0 ? (
            <div className="mt-5 rounded-3xl border-2 border-dashed border-[#147DEB]/20 bg-white p-10 text-center" data-testid="impact-donations-empty">
              <Icon name="ReceiptText" className="mx-auto h-10 w-10 text-[#94A3B8]" />
              <p className="mt-3 text-base font-semibold text-[#475569]">{t("impact.noDonations")}</p>
            </div>
          ) : (
            <div className="mt-5 overflow-x-auto rounded-3xl border-2 border-[#147DEB]/10 bg-white">
              <table className="w-full text-left text-sm">
                <thead className="bg-[#E8F5FF] text-[#082B66]">
                  <tr>
                    <th className="px-4 py-3 font-bold">#</th>
                    <th className="px-4 py-3 font-bold">Status</th>
                    <th className="px-4 py-3 font-bold">Amount</th>
                    <th className="px-4 py-3 font-bold">Tx</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.map((d, i) => (
                    <tr key={i} className="border-t border-[#147DEB]/10">
                      <td className="px-4 py-3">{d.reference}</td>
                      <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                      <td className="px-4 py-3">{d.amount ?? "—"}</td>
                      <td className="px-4 py-3">{d.explorer_url ? <a href={d.explorer_url} target="_blank" rel="noopener noreferrer" className="text-[#147DEB] underline">Explorer</a> : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Reports */}
        <div className="mt-12">
          <h3 className="font-display text-xl font-bold text-[#082B66]">{t("impact.reports")}</h3>
          <div className="mt-5 space-y-4">
            {reports.map((r) => (
              <div key={r.slug} className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-6" data-testid={`impact-report-${r.slug}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="font-display text-lg font-bold text-[#082B66]">{code === "zh" ? r.title_zh : r.title_en}</h4>
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">{code === "zh" ? r.period_zh : r.period_en}</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#475569]">{code === "zh" ? r.body_zh : r.body_en}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
